import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

import { FLASHPAY_ROLES, hasAnyRole, hasRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteChartOfAccount } from "@/features/ledger/api/use-delete-chart-of-account";
import { ChartOfAccountFormDialog } from "@/features/ledger/components/chart-of-accounts/chart-of-account-form-dialog";
import type { ChartOfAccountRow } from "@/features/ledger/components/chart-of-accounts/chart-of-accounts-columns";

/**
 * Menu d'actions de ligne du plan de comptes.
 * Édition (`PUT`) réservée à flashpay-superadmin / flashpay-admin ;
 * désactivation (`DELETE`) réservée à flashpay-superadmin (masquage —
 * le backend reste l'autorité finale).
 */
export function ChartOfAccountRowActions({
  account,
  navigate: _navigate,
}: {
  account: ChartOfAccountRow;
  navigate: NavigateFunction;
}) {
  const remove = useDeleteChartOfAccount();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = hasAnyRole([
    FLASHPAY_ROLES.SUPERADMIN,
    FLASHPAY_ROLES.ADMIN,
  ]);
  const canDelete = hasRole(FLASHPAY_ROLES.SUPERADMIN);

  return (
    <>
      <RowActionsMenu
        actions={[
          ...(canEdit
            ? [
                {
                  id: "edit",
                  label: t("ledger.accounts.actions.edit"),
                  icon: Pencil,
                  onClick: () => setEditOpen(true),
                },
              ]
            : []),
          ...(canDelete
            ? [
                {
                  id: "delete",
                  label: t("ledger.accounts.actions.delete"),
                  icon: Trash2,
                  variant: "destructive" as const,
                  onClick: () => setDeleteOpen(true),
                },
              ]
            : []),
        ]}
      />

      {canEdit ? (
        <ChartOfAccountFormDialog
          account={account}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      ) : null}

      <MutationConfirmPreview
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={{
          id: account.id,
          code: account.code,
          label: account.label,
        }}
        title={t("common.mutationPreview.deleteTitle")}
        description={t("common.mutationPreview.deleteDescription")}
        confirmLabel={t("ledger.accounts.actions.delete")}
        t={t}
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(account.id, {
            onSuccess: () => {
              notifSilence(t("ledger.accounts.feedback.deleted"));
              setDeleteOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          })
        }
      />
    </>
  );
}
